import { checkoutPayFlowController } from '@cart-services/core/checkoutPayFlowController'
import * as paymentService from '@cart-services/core/paymentService'
import { type CreditCardDialogContext } from '@cart-services/hooks/credit-card-dialog-types'
import { type CountryCode, PAYMENT_EXCEPTION_CODES } from '@constants'
import { datadogRum } from '@datadog/browser-rum'
import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { useApiFpPaymentExecution } from '@hooks-api/fortune/useApiFpPaymentExecution'
import { useApiGetFpPayment } from '@hooks-api/fortune/useApiGetFpPayment'
import { ECommerceOrderType } from '@hooks-api/types/dataModel/eCommercePayments'
import { type BaseCheckoutPayFlowModule } from '@payments/lib/checkout-pay/BaseCheckoutPayFlowModule'

export interface UseFpCampaignCreditCardDialogFlowParams {
  paymentModule: BaseCheckoutPayFlowModule
  countryCode: CountryCode
  /** 確定要整頁導頁前呼叫，供 caller 切換全頁 loading。 */
  onRedirecting: () => void
}

/**
 * Campaign CKO Flow 信用卡 dialog flow hook。訂單於進 dialog 前已由 caller 建好，
 * init 以真交易單 id（TRANSACTION）取得 session。分期不走 Flow dialog，故 installment 恆為 null。
 */
export const useFpCampaignCreditCardDialogFlow = ({
  paymentModule,
  countryCode,
  onRedirecting,
}: UseFpCampaignCreditCardDialogFlowParams) => {
  const { trigger: getPayment } = useApiGetFpPayment()
  const { trigger: executePayment } = useApiFpPaymentExecution()

  const mountCreditCardFields = useCallbackEvent(async (context: CreditCardDialogContext, containerId: string, storedCardContainerId: string) => {
    const initResult = await getPayment({
      order_type: ECommerceOrderType.TRANSACTION,
      order_id: context.paymentTransactionId,
      payment_method_id: context.paymentOptionId,
      remember_payment_card_token: context.rememberPaymentCardToken ?? false,
      installment: null,
      payment_success_url: context.paymentSuccessUrl,
      payment_fallback_url: context.paymentFallbackUrl,
      email: context.email,
    })
    const paymentInfo = initResult?.data?.data
    if (!paymentInfo) {
      throw new Error('[useFpCampaignCreditCardDialogFlow] getPayment returned empty paymentInfo')
    }
    // CkoMount 原樣交還 useCreditCardDialog 持有，dialog 收掉時由它 unmount；此處刻意不留 ref。
    return paymentService.applyCkoSessionForCreditCardDialog(paymentModule, paymentInfo, paymentModule.getAppEnv(), {
      containerId,
      storedCardContainerId,
      countryCode,
    })
  })

  const submitAndExecute = useCallbackEvent(async (context: CreditCardDialogContext) => {
    const ckoPaymentId = await paymentService.submitCreditCardDialogPayment(paymentModule, {
      paymentTransactionId: context.paymentTransactionId,
      orderHash: context.orderHash,
    })
    datadogRum.addAction('creditCardDialog.paymentCompleted', {
      paymentTransactionId: context.paymentTransactionId,
      ckoPaymentId,
    })
    const { data: paymentResult } = await paymentService.executeWithCkoReconciliationRetry(() => executePayment({
      payment_transaction_id: context.paymentTransactionId,
      service_pay_info: { bank_code: null, apple_pay_info: null, pay_token: null },
      user_payment_token_info: null,
      payment_success_url: context.paymentSuccessUrl,
      payment_fallback_url: context.paymentFallbackUrl,
      payment_option_id: context.paymentOptionId,
    }), {
      extraRetryCodes: [PAYMENT_EXCEPTION_CODES.PAYMENT_API_FAILED],
    })
    if (!paymentResult.redirect_url) {
      throw new Error('[useFpCampaignCreditCardDialogFlow] executePayment returned no redirect_url')
    }
    onRedirecting()
    window.location.replace(paymentResult.redirect_url)
  })

  const cancelInFlight = useCallbackEvent((reason: string) => {
    checkoutPayFlowController.cancelInFlight(reason)
  })

  /**
   * 卡片無效時觸發 SDK 顯示各欄位錯誤：fire-and-forget，不經 controller 的 await，
   * 因為 SDK 對無效卡不回 onPaymentCompleted / onError，走 submitAndExecute 會永久 pending 卡死彈窗。
   */
  const revealCardValidationErrors = useCallbackEvent(() => {
    void paymentModule.submit().catch((error) => {
      datadogRum.addError(error, { function: '[useFpCampaignCreditCardDialogFlow] revealCardValidationErrors' })
    })
  })

  return {
    mountCreditCardFields,
    submitAndExecute,
    cancelInFlight,
    revealCardValidationErrors,
  }
}
